import loadable from '@loadable/component';
import React from 'react';
import UniversalRouter from 'universal-router';

import { parseQueryString } from '@travel/utils';

import MyReviewLoader from 'components/MyReviewLoader';
import userProfileResolver from 'pages/MyReviewEdit/resolver';
import myReviewPageResolver from 'pages/MyReviewPage/resolver';
import PostReviewSkeleton from 'pages/PostReview/components/PostReviewSkeleton';
import postReviewResolver from 'pages/PostReview/resolver';
import ProviderReviewSkeleton from 'pages/ProviderReview/components/ProviderReviewSkeleton';
import providerResolver from 'pages/ProviderReview/resolver';

import { REVIEW_HOSTNAME } from 'constants/common';
import App from '../app';
import paths from './paths';
import { pageResolver } from './utils';

import type { Route } from 'universal-router';
import type { universalRouterProps } from './types';

export default (props: universalRouterProps) => {
  const routes: Route[] = [
    {
      path: paths.top.path,
      action: async (context) => {
        const children = await context.next();

        return <App key="app">{children}</App>;
      },
      children: [
        {
          path: paths.editReviewPage.path,
          action: async (context) => {
            const MyReviewEdit = loadable(
              () => import(/* webpackChunkName: "pages/MyReviewEdit" */ 'pages/MyReviewEdit'),
              {
                fallback: <MyReviewLoader />,
              },
            );

            await pageResolver(props, context, userProfileResolver, true);

            return <MyReviewEdit />;
          },
        },
        {
          path: [paths.myReviewPage.path, paths.otherUserReviewPage.path],
          action: async (context) => {
            const MyReviewPage = loadable(
              () => import(/* webpackChunkName: "pages/MyReviewPage" */ 'pages/MyReviewPage'),
              {
                fallback: <MyReviewLoader />,
              },
            );

            await pageResolver(props, context, myReviewPageResolver, true);

            return <MyReviewPage {...context} />;
          },
        },
        {
          path: paths.reviewPostPage.path,
          action: async (context) => {
            const PostReview = loadable(
              () => import(/* webpackChunkName: "pages/PostReview" */ 'pages/PostReview'),
              {
                fallback: <PostReviewSkeleton />,
              },
            );

            await pageResolver(props, context, postReviewResolver, true);

            return <PostReview {...context} />;
          },
        },
        {
          path: paths.reviewEditPage.path,
          action: async (context) => {
            const PostReview = loadable(
              () => import(/* webpackChunkName: "pages/PostReview" */ 'pages/PostReview'),
              {
                fallback: <PostReviewSkeleton />,
              },
            );

            await pageResolver(props, context, postReviewResolver, true);

            return <PostReview {...context} />;
          },
        },
        {
          path: [
            paths.providerReview.path, // Rerouted HTML path
            paths.providerReview.dynamicPath, // Dynamic Path
          ],
          action: async (context, params) => {
            const urlSearchParams = new URLSearchParams(context.search);
            const reviewId = urlSearchParams.get('reviewId');
            const isValidReviewId = reviewId ? /^[0-9]+$/.test(reviewId) : true;

            let errorStatus: number | undefined;
            let isError = false;

            if (params.reviewId) {
              const redirectUrl = `/hotel/voice/${params.providerId}?reviewId=${params.reviewId}`;

              if (props.res) {
                props.res.redirect(301, redirectUrl);

                return null;
              }
            }

            const isReviewHtml =
              typeof window !== 'undefined'
                ? window.location.pathname.endsWith('review.html')
                : context.req?.url?.endsWith('review.html');
            const isReviewHost =
              typeof window !== 'undefined'
                ? window.location.hostname === REVIEW_HOSTNAME
                : context.req?.hostname === REVIEW_HOSTNAME;

            if (isReviewHtml && isReviewHost) {
              errorStatus = 404;
              isError = true;
            } else if (!isValidReviewId) {
              isError = true;
            }

            if (isError) {
              if (errorStatus) props.res?.status(errorStatus);
              const ErrorPage = loadable(
                () => import(/* webpackChunkName: "pages-ErrorPage" */ 'components/ErrorPage'),
              );

              return <ErrorPage />;
            }

            const ProviderReview = loadable(
              () => import(/* webpackChunkName: "pages/ProviderReview" */ 'pages/ProviderReview'),
              {
                fallback: <ProviderReviewSkeleton />,
              },
            );

            await pageResolver(props, context, providerResolver);

            return <ProviderReview {...context} />;
          },
        },
        {
          // also resolve paths.notFound.path
          path: '(.*)',
          async action() {
            const ErrorPage = loadable(
              () => import(/* webpackChunkName: "pages-ErrorPage" */ 'components/ErrorPage'),
            );

            props.res?.status(400); //TODO: Need to check this and update accordingly

            return <ErrorPage />;
          },
        },
      ],
    },
  ];

  return new UniversalRouter(routes, {
    resolveRoute(context, params) {
      if (props.req) context.req = props.req;
      if (props.res) context.res = props.res;

      if (typeof context.route.action === 'function') {
        context.query = {};

        if (context.search) {
          // when redirect from omni with existing filter,
          // there will be ? added from the redirection
          const searchRegex = new RegExp('\\?$');
          const currentSearch = context.search;

          context.query = parseQueryString(
            searchRegex.test(currentSearch)
              ? currentSearch.substring(0, currentSearch.length - 1)
              : currentSearch,
          );
        }

        return context.route.action(context, params);
      }

      return undefined;
    },
  });
};
